import { useState } from "react";
import { ExternalLink, Info, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  EscalationSettings,
  ReportDigestFrequency,
  ReportMode,
  ReportModeSelector,
} from "@/components/agent/details/ReportModeSelector";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { useToggleAgentNotificationChannel } from "@/hooks/agent/useAgentSocials";
import { mintSuiteDeepLinkUrl } from "@/lib/suite/openSuiteApp";
import { activityService } from "@/services/analytics/ActivityService";
import { Agent } from "@/types/agent/agent";

const SYNC_APP_URL = "https://sync.helpgenie.ai/login";

/**
 * Opens this Genie's notification channels page in Help Genie Sync via SSO.
 * Opens a blank tab synchronously (inside the click handler) before the async
 * token mint, so iOS Safari doesn't block the popup — see openSuiteApp.ts.
 */
export const openGenieNotificationChannels = async (genieId: string) => {
  activityService.logSuiteAppVisit("Sync", SYNC_APP_URL, "conversation-reports-card");

  const win = window.open("about:blank", "_blank");
  if (win) {
    try {
      win.opener = null;
    } catch {
      /* cross-origin guard — safe to ignore */
    }
  }

  const url = await mintSuiteDeepLinkUrl(SYNC_APP_URL, `/app/genies/${genieId}/notifications`);
  if (win) win.location.href = url;
  else window.location.href = url;
};

interface ConversationReportsCardProps {
  agent: Agent;
  brand?: string;
}

const CONNECTION_ICONS: Record<string, string> = {
  whatsapp: "/platform-icons/WhatsApp.svg.png",
  slack: "/platform-icons/slack-new-logo-icon-11609376883z32jbkf8kg.png",
  teams: "/platform-icons/Microsoft_Office_Teams_(2018–present).svg.png",
  telegram: "/platform-icons/Telegram_2019_Logo.svg.png",
  zapier: "/platform-icons/Zapier-logo.png",
};

const MODE_LABELS: Record<ReportMode, string> = {
  auto: "Auto (AI decides)",
  every_conversation: "Every conversation",
  digest: "Digest",
  escalation_only: "Alerts only",
  off: "Off",
};

const withEscalationRecipients = (agent: Agent): EscalationSettings => ({
  ...(agent.escalation_settings ?? {}),
  recipients: { emails: agent.escalation_recipients ?? [] },
});

const describeMode = (agent: Agent): string => {
  const mode = agent.report_mode ?? (agent.send_conv_reports ? "every_conversation" : "off");
  if (mode === "digest") {
    const frequency = agent.report_digest_frequency;
    return frequency ? `Digest: ${frequency.charAt(0).toUpperCase()}${frequency.slice(1)}` : "Digest";
  }
  return MODE_LABELS[mode];
};

export const ConversationReportsCard = ({ agent, brand = "#4E9CFF" }: ConversationReportsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(
    agent.report_mode ?? (agent.send_conv_reports ? "every_conversation" : "off"),
  );
  const [digestFrequency, setDigestFrequency] = useState<ReportDigestFrequency | null>(
    agent.report_digest_frequency ?? null,
  );
  const [digestDayOfWeek, setDigestDayOfWeek] = useState<number | null>(agent.report_digest_day_of_week ?? 1);
  const [escalationSettings, setEscalationSettings] = useState<EscalationSettings>(withEscalationRecipients(agent));
  const [emails, setEmails] = useState<string[]>(agent.report_recipients ?? []);
  const [decisionPrompt, setDecisionPrompt] = useState<string | null>(agent.auto_report_decision_prompt ?? null);

  const updateAgent = useUpdateAgent();
  const toggleMutation = useToggleAgentNotificationChannel();
  const connectedChannels = agent.connected_channels ?? [];

  const isEnabled = (channelType: string) =>
    connectedChannels.find((c) => c.type === channelType)?.status === "connected";
  const hasSlackConnection = connectedChannels.some((c) => c.type === "slack");

  const handleEdit = () => {
    const currentMode = agent.report_mode ?? (agent.send_conv_reports ? "every_conversation" : "off");
    setReportMode(currentMode);
    setDigestFrequency(agent.report_digest_frequency ?? null);
    setDigestDayOfWeek(agent.report_digest_day_of_week ?? 1);
    setEscalationSettings(withEscalationRecipients(agent));
    setEmails(agent.report_recipients ?? []);
    setDecisionPrompt(agent.auto_report_decision_prompt ?? null);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        send_conv_reports: reportMode === "every_conversation",
        report_mode: reportMode,
        report_digest_frequency: reportMode === "digest" ? digestFrequency : null,
        report_digest_day_of_week: reportMode === "digest" ? digestDayOfWeek : null,
        escalation_settings: escalationSettings,
        auto_report_decision_prompt: reportMode === "auto" ? decisionPrompt : agent.auto_report_decision_prompt,
        settings: {
          ...agent.settings,
          support_team_emails: emails.filter((e) => e.trim()),
        },
      },
      // support_team_emails above is consumed by handle-agent/update.ts and persisted
      // to notification_recipients, not the settings JSON column.
    });
    toast.success("Report settings saved");
    setIsEditing(false);
  };

  const handleChannelToggle = async (channelType: string, currentlyEnabled: boolean, channelId: string) => {
    await toggleMutation.mutateAsync({ agentId: agent.id, channelId, channelType, enabled: !currentlyEnabled });
  };

  return (
    <TooltipProvider>
      <OverviewSectionCard
        title="Conversation Reports"
        subtitle="Summaries and alerts for your team"
        genieIcon="bar-chart"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
        onEdit={isEditing ? undefined : handleEdit}
        onSave={isEditing ? handleSave : undefined}
        onCancel={isEditing ? handleCancel : undefined}
        isSaving={updateAgent.isPending}
      >
        {isEditing ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Mode</label>
                <ReportModeSelector
                  reportMode={reportMode}
                  onReportModeChange={setReportMode}
                  digestFrequency={digestFrequency}
                  onDigestFrequencyChange={setDigestFrequency}
                  digestDayOfWeek={digestDayOfWeek}
                  onDigestDayOfWeekChange={setDigestDayOfWeek}
                  escalationSettings={escalationSettings}
                  onEscalationSettingsChange={setEscalationSettings}
                  hasSlackConnection={hasSlackConnection}
                  decisionPrompt={decisionPrompt}
                  onDecisionPromptChange={setDecisionPrompt}
                  onRegenerateDecisionPrompt={() => setDecisionPrompt(null)}
                  brand={brand}
                />
              </div>

              {(reportMode === "auto" || reportMode === "every_conversation" || reportMode === "digest") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                  <div className="space-y-2">
                    {emails.map((email, index) => (
                      <div key={index} className="flex gap-2 min-w-0">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const next = [...emails];
                            next[index] = e.target.value;
                            setEmails(next);
                          }}
                          className="flex-1 min-w-0 max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="support@example.com"
                        />
                        <button
                          type="button"
                          onClick={() => setEmails(emails.filter((_, i) => i !== index))}
                          className="flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200 text-sm shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setEmails([...emails, ""])}>
                      + Add Email
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-700">Reporting Status</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs max-w-xs">
                        Turn reporting on to receive summaries of conversations this genie has with customers. These
                        summaries are sent to your team via email and the channels you've configured below.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Badge
                    variant="outline"
                    className="ml-auto"
                    style={
                      (agent.report_mode ?? "off") !== "off"
                        ? { borderColor: `${brand}55`, color: brand, backgroundColor: `${brand}10` }
                        : undefined
                    }
                  >
                    {describeMode(agent)}
                  </Badge>
                </div>
                <p className="text-xs text-gray-600">
                  {(agent.report_mode ?? "auto") === "auto"
                    ? "✓ AI decides what needs your attention — immediate alerts plus a daily summary of everything"
                    : agent.report_mode === "every_conversation"
                      ? "✓ Your team will receive conversation summaries after each interaction"
                      : agent.report_mode === "digest"
                        ? "✓ Your team will receive a batched digest on a recurring schedule"
                        : agent.report_mode === "escalation_only"
                          ? "✓ Your team will be alerted only when a conversation needs attention"
                          : "✗ Conversation summaries are not being sent. Enable above to start receiving updates"}
                </p>

                {(agent.report_mode ?? "auto") === "auto" && (
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-semibold text-gray-700">Decision prompt</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-xs">
                            Tells the AI what counts as needing your attention vs. routine vs. irrelevant for this
                            genie. Edit above to customize it for your business.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {agent.auto_report_decision_prompt ? (
                      <p className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-2.5 max-h-32 overflow-y-auto">
                        {agent.auto_report_decision_prompt}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 italic">
                        No prompt yet — a default will be generated automatically the first time a conversation is
                        analyzed.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                  <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-900">
                    <p className="font-medium mb-1">Customers can also receive reports</p>
                    <p>
                      Customers can request the agent to send them a copy of the conversation report during the chat.
                      You can customize email templates in{" "}
                      <a href="/genie-hub/email-templates" className="font-medium underline hover:text-blue-700">
                        Email Templates
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              {((agent.report_recipients ?? []).length > 0 || (agent.blocked_report_recipients ?? []).length > 0) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-700">Email Recipients</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">
                          These email addresses will receive conversation reports. Add or remove team members who should
                          be notified of interactions.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Team members who receive email summaries</p>
                  <div className="space-y-1">
                    {(agent.report_recipients ?? []).map((email: string) => (
                      <div
                        key={email}
                        className="flex items-center gap-2 text-sm text-gray-600 rounded px-3 py-1.5 border"
                        style={{ borderColor: `${brand}30`, backgroundColor: `${brand}08` }}
                      >
                        <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: brand }} />
                        <CopyableEmail email={email} />
                      </div>
                    ))}
                    {(agent.blocked_report_recipients ?? []).map((email: string) => (
                      <div
                        key={email}
                        className="flex items-center gap-2 text-sm text-gray-500 rounded px-3 py-1.5 border border-red-200 bg-red-50"
                      >
                        <Mail className="w-3.5 h-3.5 shrink-0 text-red-400" />
                        <CopyableEmail email={email} />
                        <Badge variant="outline" className="ml-auto border-red-300 text-red-600 bg-red-100">
                          Blocked — bounced
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-700">Notification Channels</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-xs">
                      Select which platforms (Slack, Teams, WhatsApp, etc.) should receive conversation notifications.
                      You must set up these channels first in Help Genie Sync.
                    </p>
                  </TooltipContent>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => openGenieNotificationChannels(agent.id)}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  style={{ color: brand }}
                >
                  Edit in Help Genie Sync
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Choose which platforms deliver conversation alerts to your team
              </p>

              {connectedChannels.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {connectedChannels.map((channel) => {
                    const iconPath = CONNECTION_ICONS[channel.type] ?? null;
                    const enabled = isEnabled(channel.type);
                    return (
                      <div
                        key={channel.type}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${enabled ? "bg-white shadow-sm" : "bg-gray-50 border-gray-200 opacity-60"}`}
                        style={enabled ? { borderColor: `${brand}40` } : undefined}
                      >
                        <div
                          className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded ${enabled ? "bg-gray-50" : "bg-gray-100"}`}
                        >
                          {iconPath ? (
                            <img src={iconPath} alt={channel.type} className="w-5 h-5 object-contain" />
                          ) : (
                            <div className="w-5 h-5 bg-gray-300 rounded" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-sm font-medium capitalize truncate ${enabled ? "text-gray-900" : "text-gray-500"}`}
                          >
                            {channel.type}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{channel.name}</div>
                          {channel.receiver && <div className="text-xs text-gray-400 truncate">{channel.receiver}</div>}
                          {channel.status === "attention" && (
                            <div className="text-xs text-red-500 truncate">Needs reconnecting</div>
                          )}
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={() => handleChannelToggle(channel.type, enabled, channel.id)}
                          disabled={toggleMutation.isPending || channel.status === "attention"}
                          style={enabled ? { backgroundColor: brand } : undefined}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="text-center py-4 rounded-lg border"
                  style={{ borderColor: `${brand}30`, backgroundColor: `${brand}08` }}
                >
                  <p className="text-sm text-gray-600 mb-1">No notification channels set up yet</p>
                  <button
                    type="button"
                    onClick={() => openGenieNotificationChannels(agent.id)}
                    className="text-xs font-medium hover:underline"
                    style={{ color: brand }}
                  >
                    → Set up notification channels
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </OverviewSectionCard>
    </TooltipProvider>
  );
};
